import { useMemo, useState } from 'react';
import { ChevronRight, ChevronDown, MoreHorizontal, Circle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { Task, TaskStatus } from '@/types/index';

// ─── Types ──────────────────────────────────────────────────────────────────

interface TreeTableProps {
  tasks: Task[];
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  onSelectTask: (id: string) => void;
  selectedTaskId?: string | null;
  onEditTask: (id: string) => void;
  onAddSubtask: (id: string) => void;
  onDeleteTask: (id: string) => void;
}

interface FlatRow {
  task: Task;
  depth: number;
  hasChildren: boolean;
  completedCount: number;
  totalCount: number;
}

// ─── Status config ──────────────────────────────────────────────────────────

const statusConfig: Record<
  TaskStatus,
  { label: string; classes: string }
> = {
  pending: {
    label: 'Pending',
    classes:
      'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  },
  in_progress: {
    label: 'In Progress',
    classes:
      'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  },
  blocked: {
    label: 'Blocked',
    classes:
      'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  },
  completed: {
    label: 'Completed',
    classes:
      'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  },
  cancelled: {
    label: 'Cancelled',
    classes:
      'bg-gray-100 text-gray-500 line-through',
  },
};

// ─── Depth border colors ────────────────────────────────────────────────────

const depthBorderClasses: Record<number, string> = {
  0: '',
  1: 'border-l-2 border-blue-200 dark:border-blue-800',
  2: 'border-l-2 border-purple-200 dark:border-purple-800',
  3: 'border-l-2 border-green-200 dark:border-green-800',
};

const defaultDepthBorder = 'border-l-2 border-gray-200 dark:border-gray-800';

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildChildrenMap(tasks: Task[]): Map<string, Task[]> {
  const map = new Map<string, Task[]>();
  for (const task of tasks) {
    if (task.parentId) {
      const list = map.get(task.parentId);
      if (list) {
        list.push(task);
      } else {
        map.set(task.parentId, [task]);
      }
    }
  }
  return map;
}

function flattenTree(
  roots: Task[],
  childrenMap: Map<string, Task[]>,
  expandedIds: Set<string>,
): FlatRow[] {
  const rows: FlatRow[] = [];

  function walk(task: Task, depth: number) {
    const children = childrenMap.get(task._id) ?? [];
    const hasChildren = children.length > 0;
    const totalCount = children.length;
    const completedCount = children.filter(
      (c) => c.status === 'completed',
    ).length;

    rows.push({ task, depth, hasChildren, completedCount, totalCount });

    if (hasChildren && expandedIds.has(task._id)) {
      for (const child of children) {
        walk(child, depth + 1);
      }
    }
  }

  for (const root of roots) {
    walk(root, root.depth ?? 0);
  }

  return rows;
}

function formatRelativeTime(dateStr: string): string {
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
  } catch {
    return dateStr;
  }
}

function truncateId(id: string, maxLen = 8): string {
  return id.length > maxLen ? `${id.slice(0, maxLen)}…` : id;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function TreeTable({
  tasks,
  expandedIds,
  onToggleExpand,
  onSelectTask,
  selectedTaskId,
  onEditTask,
  onAddSubtask,
  onDeleteTask,
}: TreeTableProps) {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Build tree structure
  const { roots, rows } = useMemo(() => {
    const childrenMap = buildChildrenMap(tasks);
    const taskIds = new Set(tasks.map((t) => t._id));
    const rootTasks = tasks.filter(
      (t) => !t.parentId || !taskIds.has(t.parentId),
    );
    const flatRows = flattenTree(rootTasks, childrenMap, expandedIds);
    return { roots: rootTasks, rows: flatRows };
  }, [tasks, expandedIds]);

  // Empty state
  if (tasks.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
        No tasks found
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        {/* ── Header ─────────────────────────────────────────────────── */}
        <thead className="sticky top-0 z-10 bg-background">
          <tr className="border-b bg-muted/50">
            <th className="h-10 px-4 text-left font-medium text-muted-foreground">
              Title
            </th>
            <th className="h-10 px-4 text-left font-medium text-muted-foreground">
              Status
            </th>
            <th className="h-10 px-4 text-left font-medium text-muted-foreground">
              Owner
            </th>
            <th className="h-10 px-4 text-left font-medium text-muted-foreground">
              Progress
            </th>
            <th className="h-10 px-4 text-left font-medium text-muted-foreground">
              Created
            </th>
            <th className="h-10 w-12 px-4 text-left font-medium text-muted-foreground" />
          </tr>
        </thead>

        {/* ── Body ───────────────────────────────────────────────────── */}
        <tbody>
          {rows.map(({ task, depth, hasChildren, completedCount, totalCount }) => {
            const isSelected = task._id === selectedTaskId;
            const isExpanded = expandedIds.has(task._id);
            const statusCfg = statusConfig[task.status] ?? statusConfig.pending;
            const borderClass =
              depthBorderClasses[depth] ?? defaultDepthBorder;
            const progressPct =
              totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

            return (
              <tr
                key={task._id}
                className={cn(
                  'border-b transition-colors hover:bg-muted/30',
                  isSelected && 'bg-muted/50',
                  borderClass,
                )}
              >
                {/* Title column — includes chevron + indentation + status dot + title */}
                <td className="px-4 py-2.5">
                  <div
                    className="flex items-center gap-2"
                    style={{ paddingLeft: `${depth * 24}px` }}
                  >
                    {/* Expand chevron */}
                    <button
                      type="button"
                      className={cn(
                        'flex h-5 w-5 shrink-0 items-center justify-center rounded transition-colors hover:bg-muted',
                        !hasChildren && 'invisible',
                      )}
                      onClick={() => onToggleExpand(task._id)}
                      aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                    </button>

                    {/* Depth border indicator */}
                    {depth > 0 && (
                      <span
                        className={cn(
                          'inline-block h-4 w-0.5 shrink-0 rounded-full',
                          depth === 1 && 'bg-blue-300 dark:bg-blue-700',
                          depth === 2 && 'bg-purple-300 dark:bg-purple-700',
                          depth === 3 && 'bg-green-300 dark:bg-green-700',
                          depth >= 4 && 'bg-gray-300 dark:bg-gray-700',
                        )}
                      />
                    )}

                    {/* Status dot */}
                    <Circle
                      className={cn(
                        'h-2.5 w-2.5 shrink-0 fill-current',
                        task.status === 'pending' &&
                          'text-gray-400 dark:text-gray-500',
                        task.status === 'in_progress' &&
                          'text-blue-500 dark:text-blue-400',
                        task.status === 'blocked' &&
                          'text-red-500 dark:text-red-400',
                        task.status === 'completed' &&
                          'text-green-500 dark:text-green-400',
                        task.status === 'cancelled' &&
                          'text-gray-400 dark:text-gray-600',
                      )}
                    />

                    {/* Title */}
                    <button
                      type="button"
                      className={cn(
                        'truncate text-left cursor-pointer hover:underline',
                        task.status === 'cancelled' &&
                          'line-through text-muted-foreground',
                      )}
                      onClick={() => onSelectTask(task._id)}
                    >
                      {task.title}
                    </button>
                  </div>
                </td>

                {/* Status badge */}
                <td className="px-4 py-2.5">
                  <span
                    className={cn(
                      'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                      statusCfg.classes,
                    )}
                  >
                    {statusCfg.label}
                  </span>
                </td>

                {/* Owner */}
                <td className="px-4 py-2.5">
                  <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                    {truncateId(task.ownerId)}
                  </span>
                </td>

                {/* Progress */}
                <td className="px-4 py-2.5">
                  {hasChildren ? (
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-[60px] overflow-hidden rounded-full bg-muted">
                        <div
                          className={cn(
                            'h-full rounded-full transition-all',
                            progressPct === 100
                              ? 'bg-green-500 dark:bg-green-400'
                              : 'bg-blue-500 dark:bg-blue-400',
                          )}
                          style={{ width: `${progressPct}%` }}
                        />
                      </div>
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {completedCount}/{totalCount}
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>

                {/* Created */}
                <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
                  {formatRelativeTime(task.createdAt)}
                </td>

                {/* Actions */}
                <td className="px-4 py-2.5">
                  <DropdownMenu
                    open={openMenuId === task._id}
                    onOpenChange={(open) =>
                      setOpenMenuId(open ? task._id : null)
                    }
                  >
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Actions</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onSelect={() => onEditTask(task._id)}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditTask(task._id);
                        }}
                      >
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => onAddSubtask(task._id)}
                        onClick={(e) => {
                          e.stopPropagation();
                          onAddSubtask(task._id);
                        }}
                      >
                        Add subtask
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onSelect={() => onDeleteTask(task._id)}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteTask(task._id);
                        }}
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
